import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { UserClub } from '../types';
import { clubService } from '../services/club';
import { useUser } from './UserContext';
import { createLogger } from '../utils/logger';

const logger = createLogger('UserClubsContext');

interface UserClubsContextValue {
  userClubs: UserClub[];
  loading: boolean;
  refreshClubs: () => Promise<void>;
}

const UserClubsContext = createContext<UserClubsContextValue | undefined>(undefined);

interface UserClubsProviderProps {
  children: ReactNode;
}

export const UserClubsProvider: React.FC<UserClubsProviderProps> = ({ children }) => {
  const { user } = useUser();
  const [userClubs, setUserClubs] = useState<UserClub[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshClubs = useCallback(async () => {
    if (!user) {
      setUserClubs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const clubs = await clubService.getUserClubs(user.id);
      setUserClubs(clubs);
    } catch (error) {
      logger.error('Failed to refresh clubs:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshClubs();
    } else {
      setUserClubs([]);
      setLoading(false);
    }
  }, [user, refreshClubs]);

  return (
    <UserClubsContext.Provider value={{ userClubs, loading, refreshClubs }}>
      {children}
    </UserClubsContext.Provider>
  );
};

export const useUserClubs = (): UserClubsContextValue => {
  const context = useContext(UserClubsContext);
  if (context === undefined) {
    throw new Error('useUserClubs must be used within a UserClubsProvider');
  }
  return context;
};
